import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useCreateReview } from "@/api/hooks/useReviews";
import { REVIEW_PLATFORM_LABELS, ReviewPlatformSchema, type ReviewPlatform } from "@/api/types/review";
import { useEvents } from "@/api/hooks/useEvents";
import { formatShortDate } from "@/features/events/eventDate";

const PLATFORMS = ReviewPlatformSchema.options;

interface LogReviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Preselects the event link — used when opened from an event's detail page. */
  defaultEventId?: string;
}

/**
 * Logs a manual review (POST /v2/reviews). Per the API contract the create
 * body never carries a contact_id — only platform/url/rating/event_id/
 * received_at/notes — so this dialog has no contact picker (mirrors
 * RecordPaymentDialog's dollars-at-submit-only pattern for the same reason:
 * nothing here is a value the API stores as a client-side computation).
 */
export function LogReviewDialog({ open, onClose, defaultEventId }: LogReviewDialogProps) {
  const [platform, setPlatform] = useState<ReviewPlatform>("google");
  const [url, setUrl] = useState("");
  const [rating, setRating] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  const [eventId, setEventId] = useState("");
  const [notes, setNotes] = useState("");

  const createReview = useCreateReview();
  // A reasonably-sized recent-events list for the optional event picker —
  // not paginated further here, mirroring the simple single-page dropdown
  // convention (no infinite-scroll picker component exists yet in this app).
  const { data: eventsData } = useEvents({ limit: 50 });
  const events = useMemo(() => eventsData?.pages.flatMap((p) => p.items) ?? [], [eventsData]);

  useEffect(() => {
    if (open) {
      setPlatform("google");
      setUrl("");
      setRating("");
      setReceivedAt("");
      setEventId(defaultEventId ?? "");
      setNotes("");
    }
  }, [open, defaultEventId]);

  const canSubmit = url.trim().length > 0 && !createReview.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await createReview.mutateAsync({
        platform,
        url: url.trim(),
        rating: rating.trim() ? Number(rating) : undefined,
        event_id: eventId || undefined,
        received_at: receivedAt || undefined,
        notes: notes.trim() || undefined,
      });
      toastSuccess("Review logged");
      onClose();
    } catch (err) {
      toastError("Couldn't log review", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Log review">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Log review</DialogHeader>
        <DialogBody className="space-y-4">
          <FormSelect
            label="Platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as ReviewPlatform)}
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {REVIEW_PLATFORM_LABELS[p]}
              </option>
            ))}
          </FormSelect>

          <FormField
            label="URL"
            required
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />

          <FormField
            label="Rating (optional)"
            type="number"
            min={1}
            max={5}
            step={1}
            placeholder="1-5"
            value={rating}
            onChange={(e) => setRating(e.target.value)}
          />

          <FormField
            label="Received on (optional)"
            type="date"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            hint="Leave blank to use today."
          />

          <FormSelect
            label="Event (optional)"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
          >
            <option value="">No event link</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {formatShortDate(event.event_date)}
                {event.venue_name ? ` — ${event.venue_name}` : ""}
              </option>
            ))}
          </FormSelect>

          <FormTextarea
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createReview.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createReview.isPending ? "Saving..." : "Log review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
